import type { 
  ElementType, 
  ElementConfig, 
  InjectionResult, 
  StyleCustomization 
} from './types';
import { getFinalStyles } from './utils';
import { DEFAULT_MODAL_CONFIG } from './types';

/**
 * Main class for managing MellowTel UI elements
 */
export class MellowtelElements {
  private readonly baseUrl: string = 'https://www.mellow.tel';
  private readonly extensionId: string;
  private readonly configKey: string;

  constructor(extensionId: string, configKey: string) {
    if (!extensionId || extensionId.trim() === '') {
      throw new Error('Extension ID is required and cannot be empty');
    }
    if (!configKey || configKey.trim() === '') {
      throw new Error('Config key is required and cannot be empty');
    }
    
    this.extensionId = extensionId.trim();
    this.configKey = configKey.trim();
  }

  /**
   * Create an element into a container by ID
   * @param containerId - The ID of the container element
   * @param config - Element configuration
   * @returns Promise resolving to injection result
   */
  async createElement(containerId: string, config: ElementConfig): Promise<InjectionResult> {
    try {
      const container = document.getElementById(containerId);
      
      if (!container) {
        return {
          success: false,
          error: `Container element with ID '${containerId}' not found`
        };
      }

      const iframe = this.createIframe(config);
      container.appendChild(iframe);

      return {
        success: true,
        iframe
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Create and configure an iframe element
   * @param config - Element configuration
   * @returns Configured iframe element
   */
  private createIframe(config: ElementConfig): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    
    // Get final styles (defaults merged with user styles) for iframe sizing
    const finalStyles = getFinalStyles(config.type, config.styles);
    const width = finalStyles.width ? `${finalStyles.width}px` : '100%';
    const height = finalStyles.height ? `${finalStyles.height}px` : '60px';
    
    iframe.style.width = width;
    iframe.style.height = height;
    iframe.style.border = 'none';
    iframe.style.display = 'block';

    // Build the iframe URL
    const url = this.buildElementUrl(config);
    iframe.src = url;

    // Set iframe attributes for security and functionality
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    
    return iframe;
  }

  /**
   * Build the URL for the iframe based on configuration
   * @param config - Element configuration
   * @returns Complete URL for the iframe
   */
  private buildElementUrl(config: ElementConfig): string {
    const endpoint = this.getElementEndpoint(config.type);
    const url = new URL(`${this.baseUrl}${endpoint}`);

    // Add extension ID and config key as parameters
    url.searchParams.set('extension_id', this.extensionId);
    url.searchParams.set('config_key', this.configKey);

    // Get final styles (defaults merged with user styles)
    const finalStyles = getFinalStyles(config.type, config.styles);
    this.addStyleParams(url, finalStyles);

    return url.toString();
  }

  /**
   * Get the endpoint path for a given element type
   * @param type - Element type
   * @returns Endpoint path
   */
  private getElementEndpoint(type: ElementType): string {
    const endpoints: Record<ElementType, string> = {
      'opt-in': '/elements/opt-in',
      'opt-out': '/elements/opt-out',
      'settings': '/elements/settings',
    };

    return endpoints[type];
  }

  /**
   * Add style customization parameters to URL
   * @param url - URL object to modify
   * @param styles - Style customization options
   */
  private addStyleParams(url: URL, styles: StyleCustomization): void {
    if (styles.textColor) {
      url.searchParams.set('textColor', styles.textColor);
    }
    
    if (styles.backgroundColor) {
      url.searchParams.set('backgroundColor', styles.backgroundColor);
    }
    
    if (styles.radius !== undefined) {
      url.searchParams.set('radius', styles.radius.toString());
    }
    
    if (styles.width !== undefined) {
      url.searchParams.set('width', styles.width.toString());
    }
    
    if (styles.height !== undefined) {
      url.searchParams.set('height', styles.height.toString());
    }
  }

  /**
   * Remove an injected element from its container
   * @param containerId - The ID of the container element
   * @returns Whether removal was successful
   */
  removeElement(containerId: string): boolean {
    try {
      const container = document.getElementById(containerId);
      if (!container) {
        return false;
      }

      const iframe = container.querySelector('iframe');
      if (iframe) {
        iframe.remove();
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * Creates and injects a modal in the top right corner of the page
   * Uses Shadow DOM for style isolation
   * Supports snoozing with Chrome storage or localStorage fallback
   * @param inviteId - Invite ID for composing the redirect link
   * @param title - Optional title for the modal
   * @param body - Text content to display in the modal
   */
  async createModal(
    inviteId: string,
    title?: string,
    body?: string
  ): Promise<void> {
    // Use provided values or fall back to defaults
    const modalTitle = title ?? DEFAULT_MODAL_CONFIG.title;
    const modalBody = body ?? DEFAULT_MODAL_CONFIG.body;

    // Fetch extension details using the class's extensionId and configKey
    let extensionDetails = { name: 'Support with Mellowtel', icon: '' };
    if (this.extensionId && this.configKey) {
      extensionDetails = await this.getExtensionDetails();
    }

    // Storage utility functions
    const storage = {
      // Check if chrome.storage.local is available
      isChrome: !!(typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local),

      async get(key: string) {
        if (this.isChrome) {
          return new Promise((resolve) => {
            chrome.storage.local.get([key], (result) => {
              resolve(result[key]);
            });
          });
        } else {
          const value = localStorage.getItem(key);
          return value ? JSON.parse(value) : undefined;
        }
      },

      async set(key: string, value: any) {
        if (this.isChrome) {
          return new Promise((resolve) => {
            chrome.storage.local.set({ [key]: value }, resolve as () => void);
          });
        } else {
          localStorage.setItem(key, JSON.stringify(value));
          return Promise.resolve();
        }
      }
    };

    // Check if modal is snoozed
    const SNOOZE_KEY = 'mellowtel_modal_snooze';

    const isModalSnoozed = async () => {
      try {
        const snoozeData = await storage.get(SNOOZE_KEY);
        if (!snoozeData) return false;

        const now = new Date().getTime();
        const snoozeUntil = snoozeData.until;

        if (snoozeData.disabled) {
          return true; // Permanently disabled
        }

        if (snoozeUntil && now < snoozeUntil) {
          return true; // Still snoozed
        }

        // Snooze period expired, clear the data
        if (snoozeUntil && now >= snoozeUntil) {
          await storage.set(SNOOZE_KEY, null);
        }

        return false;
      } catch (error) {
        console.warn('Error checking snooze status:', error);
        return false;
      }
    };

    const setSnooze = async (option: string) => {
      try {
        const now = new Date().getTime();
        let snoozeData = {};

        switch (option) {
          case '1 week':
            snoozeData = {
              until: now + (7 * 24 * 60 * 60 * 1000), // 1 week in milliseconds
              disabled: false
            };
            break;
          case '2 weeks':
            snoozeData = {
              until: now + (14 * 24 * 60 * 60 * 1000), // 2 weeks in milliseconds
              disabled: false
            };
            break;
          case 'Disable':
            snoozeData = {
              until: null,
              disabled: true
            };
            break;
          default:
            return;
        }

        await storage.set(SNOOZE_KEY, snoozeData);
      } catch (error) {
        console.error('Error setting snooze:', error);
      }
    };

    // Check if modal should be shown
    const snoozed = await isModalSnoozed();
    if (snoozed) {
      return;
    }

    // Show the modal
    this.showModal(modalTitle, modalBody, extensionDetails, inviteId, setSnooze);
  }

  /**
   * Fetches extension details from the API
   */
  private async getExtensionDetails(): Promise<{name: string, icon: string}> {
    try {
      const url = `https://brnxqa5o266zljl4p7odbvvtde0huoeo.lambda-url.us-east-1.on.aws/?extension_id=${encodeURIComponent(this.extensionId)}&configuration_key=${encodeURIComponent(this.configKey)}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch extension details: ${response.status}`);
      }

      const data = await response.json();
      return {
        name: data.name || 'Extension',
        icon: data.icon || '',
      };
    } catch (error) {
      console.warn('Error fetching extension details:', error);
      // Return fallback data
      return {
        name: 'Extension',
        icon: '',
      };
    }
  }

  /**
   * Shows the modal with the provided configuration
   */
  private showModal(
    modalTitle: string,
    modalBody: string,
    extensionDetails: {name: string, icon: string},
    inviteId: string,
    setSnooze: (option: string) => Promise<void>
  ): void {
    // Create the modal container element
    const modalContainer = document.createElement('div');
    modalContainer.id = 'mellowtel-modal-container';

    // Create shadow root for style isolation
    const shadowRoot = modalContainer.attachShadow({ mode: 'closed' });

    // Define the modal HTML structure
    const modalHTML = `
            <div class="modal-overlay">
                <div class="modal">
                    <div class="modal-header">
                        <h3 class="modal-title">${modalTitle}</h3>
                        <button class="close-button" aria-label="Close modal">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p class="modal-text">${modalBody}</p>
                        <a href="https://www.mellowtel.com/support-with-mellowtel" class="learn-more-link" target="_blank">Learn more</a>
                        <button class="add-button">
                            <span class="check-icon">✓</span>
                            Add Plugin
                        </button>
                    </div>
                    <div class="modal-divider"></div>
                    <div class="modal-footer">
                        <div class="logo-section">
                            ${extensionDetails.icon ? `<img src="${extensionDetails.icon}" alt="${extensionDetails.name}" class="logo-icon" />` : ''}
                            <span class="logo-text"><span class="extension-name">${extensionDetails.name}</span></span>
                        </div>
                        <button class="settings-button" aria-label="Settings">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.2583 9.77251 19.9887C9.5799 19.7191 9.31074 19.5123 9 19.39C8.69838 19.2569 8.36381 19.2172 8.03941 19.276C7.71502 19.3348 7.41568 19.4895 7.18 19.72L7.12 19.78C6.93425 19.966 6.71368 20.1135 6.47088 20.2141C6.22808 20.3148 5.96783 20.3666 5.705 20.3666C5.44217 20.3666 5.18192 20.3148 4.93912 20.2141C4.69632 20.1135 4.47575 19.966 4.29 19.78C4.10405 19.5943 3.95653 19.3737 3.85588 19.1309C3.75523 18.8881 3.70343 18.6278 3.70343 18.365C3.70343 18.1022 3.75523 17.8419 3.85588 17.5991C3.95653 17.3563 4.10405 17.1357 4.29 16.95L4.35 16.89C4.58054 16.6543 4.73519 16.355 4.794 16.0306C4.85282 15.7062 4.81312 15.3716 4.68 15.07C4.55324 14.7742 4.34276 14.522 4.07447 14.3443C3.80618 14.1666 3.49179 14.0713 3.17 14.07H3C2.46957 14.07 1.96086 13.8593 1.58579 13.4842C1.21071 13.1091 1 12.6004 1 12.07C1 11.5396 1.21071 11.0309 1.58579 10.6558C1.96086 10.2807 2.46957 10.07 3 10.07H3.09C3.42099 10.0623 3.742 9.95512 4.01165 9.76251C4.28131 9.5699 4.48815 9.30074 4.61 9C4.74312 8.69838 4.78282 8.36381 4.724 8.03941C4.66519 7.71502 4.51054 7.41568 4.28 7.18L4.22 7.12C4.03405 6.93425 3.88653 6.71368 3.78588 6.47088C3.68523 6.22808 3.63343 5.96783 3.63343 5.705C3.63343 5.44217 3.68523 5.18192 3.78588 4.93912C3.88653 4.69632 4.03405 4.47575 4.22 4.29C4.40575 4.10405 4.62632 3.95653 4.86912 3.85588C5.11192 3.75523 5.37217 3.70343 5.635 3.70343C5.89783 3.70343 6.15808 3.75523 6.40088 3.85588C6.64368 3.95653 6.86425 4.10405 7.05 4.29L7.11 4.35C7.34568 4.58054 7.64502 4.73519 7.96941 4.794C8.29381 4.85282 8.62838 4.81312 8.93 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                    <div class="submenu" style="display: none;">
                        <div class="submenu-header">Show again after:</div>
                        <div class="submenu-item">1 week</div>
                        <div class="submenu-item">2 weeks</div>
                        <div class="submenu-item">Disable</div>
                    </div>
                </div>
            </div>
        `;

    // Define the modal styles
    const modalStyles = `
            <style>
                :host {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                    z-index: 10000;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                }
                
                .modal-overlay {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    pointer-events: auto;
                    animation: slideInFromRight 0.3s ease-out;
                }
                
                .modal {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
                    min-width: 320px;
                    max-width: 380px;
                    border: 1px solid #e8e8e8;
                    overflow: hidden;
                    position: relative;
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 20px 20px 0 20px;
                    background: white;
                }
                
                .modal-title {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 400;
                    color: #333;
                    line-height: 1.4;
                    flex: 1;
                    padding-right: 10px;
                }
                
                .close-button {
                    background: none;
                    border: none;
                    font-size: 24px;
                    color: #999;
                    cursor: pointer;
                    padding: 0;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                
                .close-button:hover {
                    background-color: #f5f5f5;
                    color: #666;
                }
                
                .close-button:focus {
                    outline: 2px solid #6c5ce7;
                    outline-offset: 2px;
                }
                
                .modal-body {
                    padding: 20px;
                    padding-top: 16px;
                }
                
                .modal-text {
                    margin: 0 0 12px 0;
                    color: #555;
                    line-height: 1.5;
                    font-size: 14px;
                }
                
                .learn-more-link {
                    color: #6c5ce7;
                    text-decoration: none;
                    font-size: 14px;
                    font-weight: 500;
                    display: inline-block;
                    margin-bottom: 20px;
                }
                
                .learn-more-link:hover {
                    text-decoration: underline;
                }
                
                .add-button {
                    width: 100%;
                    background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 12px 16px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }
                
                .add-button:hover {
                    background: linear-gradient(135deg, #5a4fcf 0%, #9085e8 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
                }
                
                .check-icon {
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                    width: 18px;
                    height: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: bold;
                }
                
                .modal-divider {
                    height: 1px;
                    background: #e8e8e8;
                    margin: 0;
                }
                
                .modal-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 20px;
                    background: #fafafa;
                }
                
                .logo-section {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .logo-icon {
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    object-fit: cover;
                }
                
                .logo-text {
                    font-size: 12px;
                    font-weight: 500;
                }
                
                .extension-name {
                    color: #6c5ce7;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                }
                
                .settings-button {
                    background: none;
                    border: none;
                    color: #666;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }
                
                .settings-button:hover {
                    background-color: #e8e8e8;
                    color: #333;
                }
                
                .settings-button:focus {
                    outline: 2px solid #6c5ce7;
                    outline-offset: 2px;
                }
                
                .submenu {
                    position: absolute;
                    bottom: 60px;
                    right: 20px;
                    background: white;
                    border: 1px solid #e8e8e8;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                    min-width: 150px;
                    z-index: 10001;
                    animation: fadeInUp 0.2s ease-out;
                }
                
                .submenu-header {
                    padding: 12px 16px 8px 16px;
                    font-size: 12px;
                    color: #888;
                    font-weight: 500;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .submenu-item {
                    padding: 12px 16px;
                    font-size: 14px;
                    color: #333;
                    cursor: pointer;
                    border-bottom: 1px solid #f0f0f0;
                    transition: background-color 0.2s ease;
                }
                
                .submenu-item:last-child {
                    border-bottom: none;
                }
                
                .submenu-item:hover {
                    background-color: #f8f8f8;
                }
                
                @keyframes slideInFromRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                @keyframes slideOutToRight {
                    from {
                        transform: translateX(0);
                        opacity: 1;
                    }
                    to {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                }
                
                @keyframes fadeInUp {
                    from {
                        transform: translateY(10px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                
                .modal-overlay.closing {
                    animation: slideOutToRight 0.3s ease-in forwards;
                }
                
                /* Mobile responsiveness */
                @media (max-width: 480px) {
                    .modal-overlay {
                        top: 10px;
                        right: 10px;
                        left: 10px;
                    }
                    
                    .modal {
                        min-width: auto;
                        max-width: none;
                    }
                    
                    .submenu {
                        right: 10px;
                    }
                }
            </style>
        `;

    // Set the shadow DOM content
    shadowRoot.innerHTML = modalStyles + modalHTML;

    // Add event listeners
    const closeButton = shadowRoot.querySelector('.close-button') as HTMLButtonElement;
    const modalOverlay = shadowRoot.querySelector('.modal-overlay') as HTMLDivElement;
    const settingsButton = shadowRoot.querySelector('.settings-button') as HTMLButtonElement;
    const submenu = shadowRoot.querySelector('.submenu') as HTMLDivElement;
    const addButton = shadowRoot.querySelector('.add-button') as HTMLButtonElement;

    const closeModal = () => {
      modalOverlay.classList.add('closing');
      setTimeout(() => {
        if (modalContainer.parentNode) {
          modalContainer.parentNode.removeChild(modalContainer);
        }
      }, 300); // Match animation duration
    };

    // Close on button click
    closeButton.addEventListener('click', closeModal);

    // Settings button toggle submenu
    let isSubmenuOpen = false;
    settingsButton.addEventListener('click', (e) => {
      e.stopPropagation();
      isSubmenuOpen = !isSubmenuOpen;
      submenu.style.display = isSubmenuOpen ? 'block' : 'none';
    });

    // Close submenu when clicking outside
    document.addEventListener('click', (e) => {
      if (isSubmenuOpen && !modalContainer.contains(e.target as Node)) {
        isSubmenuOpen = false;
        submenu.style.display = 'none';
      }
    });

    // Submenu item clicks
    const submenuItems = shadowRoot.querySelectorAll('.submenu-item');
    submenuItems.forEach((item) => {
      item.addEventListener('click', async () => {
        const optionText = item.textContent!.trim();

        // Handle different snooze options
        await setSnooze(optionText);

        isSubmenuOpen = false;
        submenu.style.display = 'none';

        // Close the modal after selecting snooze option
        closeModal();
      });
    });

    // Add button click
    addButton.addEventListener('click', () => {
      if (inviteId) {
        const redirectUrl = `https://www.mellowtel.com/redirect?invite_id=${inviteId}`;
        window.open(redirectUrl, '_blank');
      } else {
        console.warn('No inviteId provided for redirect URL');
      }
    });

    // Inject the modal into the page
    document.body.appendChild(modalContainer);
  }

}
